// Reine Regeln für den Objekt-Filter, die erweiterte Freitextsuche und die
// Gruppierung/Summen je Objekt im Rechnungs-Bereich (Spec 2026-07-29).
// Bewusst ohne React/Supabase-Bezug, damit vitest-testbar.

import { trifftText } from '../suche/textSuche';

/** Schlüssel der Gruppe „ohne Objektbezug" (auch Wert des Filter-Eintrags). */
export const OHNE_OBJEKT = '__ohne_objekt__';

/** Schlüssel der Gruppe „ohne Haus" innerhalb einer Liegenschaft mit Häusern. */
export const OHNE_HAUS = '__ohne_haus__';

export interface ObjektFilterRechnung {
  objekt_id?: string | null;
  /** Display-only Auto-Match aus lib/rechnungen/objektMatch (nicht in der DB). */
  _matchedObjektId?: string | null;
  /** Display-only Auto-Match-Objektnummer (nicht in der DB). */
  _matchedObjektnummer?: string | null;
  unternehmen?: string | null;
  rechnungsnummer?: string | null;
  bezeichnung?: string | null;
  objektnummer?: string | null;
  objekt_adresse?: string | null;
  brutto?: number | null;
  status?: string | null;
  /** Optionale Haus-Ebene (objekt_haeuser); null = ohne Haus. */
  haus_id?: string | null;
}

export interface Kennzahlen { anzahl: number; brutto: number; offen: number }

export interface ObjektGruppe<T> { key: string; label: string; rechnungen: T[]; summe: Kennzahlen }

/** Objektbezug, wie ihn die Liste ANZEIGT: echte Verknüpfung, sonst Auto-Match. */
export function effektiveObjektId(r: ObjektFilterRechnung): string | null {
  return r.objekt_id || r._matchedObjektId || null;
}

/** Freitextsuche über Beleg- und Objektfelder; reine Zahleingaben treffen
 *  zusätzlich den Betrag (beide Seiten auf Ziffern reduziert, damit
 *  „1.234,56", „1234,56" und „1234.56" denselben Beleg finden).
 *  Text läuft über die gemeinsame Regel in lib/suche/textSuche: normalisiert,
 *  mehrere Wörter müssen alle treffen – auch verteilt über die Felder. */
export function passtSuche(r: ObjektFilterRechnung, begriff: string): boolean {
  const q = begriff.trim().toLowerCase();
  if (!q) return true;

  const felder = [r.unternehmen, r.rechnungsnummer, r.bezeichnung, r.objektnummer, r._matchedObjektnummer, r.objekt_adresse];
  if (trifftText(felder, q)) return true;

  if (/^[\d.,\s]+$/.test(q) && r.brutto != null) {
    const ziffern = q.replace(/\D/g, '');
    if (ziffern.length >= 2) {
      return Number(r.brutto).toFixed(2).replace(/\D/g, '').includes(ziffern);
    }
  }
  return false;
}

/** Anzahl, Brutto-Summe und offener Anteil (alles außer Status „bezahlt"). */
export function summe(rechnungen: ObjektFilterRechnung[]): Kennzahlen {
  return rechnungen.reduce<Kennzahlen>((acc, r) => {
    const brutto = Number(r.brutto) || 0;
    const bezahlt = (r.status || '').toLowerCase() === 'bezahlt';
    return {
      anzahl: acc.anzahl + 1,
      brutto: acc.brutto + brutto,
      offen: acc.offen + (bezahlt ? 0 : brutto),
    };
  }, { anzahl: 0, brutto: 0, offen: 0 });
}

/** Gruppiert nach effektivem Objekt. Reihenfolge: Label alphabetisch (de),
 *  Gruppe ohne Objekt immer zuletzt. Innerhalb der Gruppe bleibt die
 *  übergebene Reihenfolge (= aktive Sortierung der Liste) erhalten. */
export function gruppiereNachObjekt<T extends ObjektFilterRechnung>(
  rechnungen: T[],
  labelFuer: (objektId: string) => string,
): ObjektGruppe<T>[] {
  const nachObjekt = new Map<string, T[]>();
  for (const r of rechnungen) {
    const key = effektiveObjektId(r) ?? OHNE_OBJEKT;
    const liste = nachObjekt.get(key);
    if (liste) liste.push(r);
    else nachObjekt.set(key, [r]);
  }

  const gruppen: ObjektGruppe<T>[] = [...nachObjekt.entries()].map(([key, liste]) => ({
    key,
    label: key === OHNE_OBJEKT ? 'Ohne Objekt' : labelFuer(key),
    rechnungen: liste,
    summe: summe(liste),
  }));

  gruppen.sort((a, b) => {
    if (a.key === OHNE_OBJEKT) return 1;
    if (b.key === OHNE_OBJEKT) return -1;
    return a.label.localeCompare(b.label, 'de');
  });
  return gruppen;
}

/** Haus-Filter innerhalb einer gefilterten Liegenschaft. `null` = kein Haus-Filter. */
export function passtHausFilter(r: ObjektFilterRechnung, hausId: string | null): boolean {
  if (!hausId) return true;
  if (hausId === OHNE_HAUS) return !r.haus_id;
  return r.haus_id === hausId;
}

/** Gruppiert nach Haus (innerhalb einer Liegenschaft). Reihenfolge kommt aus
 *  `reihenfolge` (die sortierten Haus-IDs); „Ohne Haus" steht immer zuletzt.
 *  Häuser ohne Belege erscheinen nicht — anders als in der Einheitenliste hilft
 *  eine leere Rechnungsgruppe niemandem. */
export function gruppiereNachHaus<T extends ObjektFilterRechnung>(
  rechnungen: T[],
  reihenfolge: string[],
  labelFuer: (hausId: string) => string,
): ObjektGruppe<T>[] {
  const nachHaus = new Map<string, T[]>();
  for (const r of rechnungen) {
    const key = r.haus_id || OHNE_HAUS;
    const liste = nachHaus.get(key);
    if (liste) liste.push(r);
    else nachHaus.set(key, [r]);
  }

  const gruppen: ObjektGruppe<T>[] = [...nachHaus.entries()].map(([key, liste]) => ({
    key,
    label: key === OHNE_HAUS ? 'Ohne Haus' : labelFuer(key),
    rechnungen: liste,
    summe: summe(liste),
  }));

  const rang = new Map(reihenfolge.map((id, i) => [id, i]));
  gruppen.sort((a, b) => {
    if (a.key === OHNE_HAUS) return 1;
    if (b.key === OHNE_HAUS) return -1;
    // Unbekannte Häuser (z. B. gerade gelöscht) hinter die bekannten sortieren.
    return (rang.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (rang.get(b.key) ?? Number.MAX_SAFE_INTEGER);
  });
  return gruppen;
}
